import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  Divider,
} from "@mui/material";
import {
  Lock as LockIcon,
  CalendarMonth as CalendarMonthIcon,
  LocationOn as LocationOnIcon,
  Straighten as StraightenIcon,
  AccessTime as AccessTimeIcon,
  Star as StarIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
} from "@mui/icons-material";

/**
 * Dialog for displaying the details of a non-shared (private) dive
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Function to call when closing the dialog
 * @param {Object} props.dive - Dive data object
 */
function NonSharedDiveDetailsDialog({ open, onClose, dive }) {
  console.log("Dive data - individual dialog:", dive);
  const [expanded, setExpanded] = useState(false);

  if (!dive) return null;

  const toggleExpanded = () => setExpanded(!expanded);

  const formattedDate = dive.diveDate
    ? new Date(dive.diveDate).toLocaleDateString()
    : "Unknown date";

  // Handle the new divesite structure from your console log data
  const diveSiteName =
    dive.divesite?.uddf?.divesite?.site?.name ||
    dive.divesite?.name ||
    "Unnamed Dive Site";
  const diveSiteLocation =
    dive.divesite?.uddf?.divesite?.site?.geography?.address?.city ||
    dive.divesite?.location ||
    "Unknown Location";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">Individual Dive Details</Typography>
          <Chip
            icon={<LockIcon />}
            label="Individual"
            color="default"
            size="small"
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            {diveSiteName}
          </Typography>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  Date & Location
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <CalendarMonthIcon
                      fontSize="small"
                      sx={{ mr: 1, color: "text.secondary" }}
                    />
                    <Typography>{formattedDate}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <LocationOnIcon
                      fontSize="small"
                      sx={{ mr: 1, color: "text.secondary" }}
                    />
                    <Typography>{diveSiteLocation}</Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  Dive Statistics
                </Typography>
                <Stack spacing={1}>
                  {dive.maxDepth && (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <StraightenIcon
                        fontSize="small"
                        sx={{ mr: 1, color: "text.secondary" }}
                      />
                      <Typography>{dive.maxDepth}m max depth</Typography>
                    </Box>
                  )}
                  {dive.diveDuration && (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <AccessTimeIcon
                        fontSize="small"
                        sx={{ mr: 1, color: "text.secondary" }}
                      />
                      <Typography>{dive.diveDuration} minutes</Typography>
                    </Box>
                  )}
                  {dive.diveRating && (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <StarIcon
                        fontSize="small"
                        sx={{ mr: 1, color: "text.secondary" }}
                      />
                      <Typography>{dive.diveRating}/5 rating</Typography>
                    </Box>
                  )}
                  {dive.visibilityRating && (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <StarIcon
                        fontSize="small"
                        sx={{ mr: 1, color: "text.secondary" }}
                      />
                      <Typography>
                        {dive.visibilityRating}/5 visibility
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          {/* Additional dive details section */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {dive.buddy && (
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    gutterBottom
                  >
                    Dive Buddy
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <PersonIcon
                      fontSize="small"
                      sx={{ mr: 1, color: "text.secondary" }}
                    />
                    <Typography>
                      {dive.buddy.displayName || "Unknown Buddy"}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            )}

            {dive.tankPressureStart && dive.tankPressureEnd && (
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    gutterBottom
                  >
                    Tank Pressure
                  </Typography>
                  <Typography variant="body2">
                    Start: {dive.tankPressureStart} bar
                  </Typography>
                  <Typography variant="body2">
                    End: {dive.tankPressureEnd} bar
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
                    Used: {dive.tankPressureStart - dive.tankPressureEnd} bar
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>

          {dive.comments && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography
                variant="subtitle2"
                color="textSecondary"
                gutterBottom
              >
                Your Comments
              </Typography>
              <Typography variant="body2">
                {expanded
                  ? dive.comments
                  : dive.comments.length > 150
                  ? dive.comments.substring(0, 150) + "..."
                  : dive.comments}
              </Typography>
              {dive.comments.length > 150 && (
                <Button
                  size="small"
                  onClick={toggleExpanded}
                  sx={{ mt: 1 }}
                  endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                  {expanded ? "Show Less" : "Show More"}
                </Button>
              )}
            </Paper>
          )}

          {/* Gear section */}
          {dive.weightUsed || (dive.gearUsed && dive.gearUsed.length > 0) ? (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography
                variant="subtitle2"
                color="textSecondary"
                gutterBottom
              >
                Gear Information
              </Typography>

              {dive.weightUsed && (
                <Typography variant="body2" paragraph>
                  Weight Used: {dive.weightUsed} kg
                </Typography>
              )}

              {dive.gearUsed && dive.gearUsed.length > 0 && (
                <>
                  <Typography variant="body2" gutterBottom>
                    Gear Used:
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {dive.gearUsed.map((gear, index) => (
                      <Chip
                        key={index}
                        label={gear}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ mb: 0.5 }}
                      />
                    ))}
                  </Box>
                </>
              )}
            </Paper>
          ) : null}

          {/* Sea life sightings section */}
          {dive.seenItems && dive.seenItems.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography
                variant="subtitle2"
                color="textSecondary"
                gutterBottom
              >
                Sea Life Sightings
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {dive.seenItems.map((item, index) => (
                  <Chip
                    key={index}
                    label={item.commonName || item.name || "Unknown Creature"}
                    size="small"
                    color="secondary"
                    variant="outlined"
                    sx={{ mb: 0.5 }}
                  />
                ))}
              </Box>
            </Paper>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default NonSharedDiveDetailsDialog;
